// @ts-check
/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import logo from '../../img/CST-new-logo.svg';
import TermsAndConditionsPage from './TermsAndConditionsPage';
import { countries, termsAndConditions } from '../../data';
import StartButton from './StartButton';
import DataProtectionStatement from './DataProtectionStatement';

/** The WebStorageAPI has mechanisms to store data as key/value pairs, in a intuitive fashion */
const db = window.localStorage;
const none = 'none';
const USER = {
  acceptedTermsAndConditions: 'acceptedTermsAndConditions',
  country: 'country',
};

class StartPage extends React.PureComponent {
  // TODO: Consider adding an error boundary to your tree to customize error handling behavior.
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
    this.handleAcceptTermsClick = this.handleAcceptTermsClick.bind(this);
    this.getPreviousData = this.getPreviousData.bind(this);
    this.getVersion = this.getVersion.bind(this);

    this.state = this.getPreviousData();
    this.countriesOptions = countries.map(c => (
      <option key={c.id} value={c.id}>
        {c.id}
      </option>
    ));

    document.documentElement.classList.add('html-bg');
    document.body.classList.add('body-bg');
  }

  componentWillUnmount() {
    document.documentElement.classList.remove('html-bg');
    document.body.classList.remove('body-bg');
  }

  /** Get the User data object from LocalStorage */
  getPreviousData = () => {
    const acceptedTermsAndConditions = db.getItem(USER.acceptedTermsAndConditions);
    const country = db.getItem(USER.country);

    if (country === null && acceptedTermsAndConditions === null) {
      return {
        // if there's no previos data, set initial country to none
        country: none,
        acceptedTermsAndConditions: false,
      };
    }

    if (acceptedTermsAndConditions === null) {
      return {
        country,
        acceptedTermsAndConditions: false,
      };
    }

    if (country === null) {
      return {
        country: none,
        acceptedTermsAndConditions: true,
        acceptedDate: acceptedTermsAndConditions,
      };
    }

    return {
      country,
      acceptedTermsAndConditions: true,
      acceptedDate: acceptedTermsAndConditions,
    };
  };

  getVersion = (country) => countries.find(c => c.id === country).version;

  /**
   * onSubmit event when clicking the button of the form
   * Saves the data provided by the user to LocalStorage and redirects
   * to the Wizard/Inital questions.
   */
  handleSubmit = (event) => {
    event.preventDefault();
    const { country } = this.state;
    // eslint-disable-next-line react/destructuring-assignment
    this.props.onReady(this.getVersion(country));
  };

  saveData = (key, value) => {
    try {
      db.setItem(key, value);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  /**
   * Generic function to handle all types of onChange
   * Modifies state based on the target.
   */
  handleChange = (event) => {
    event.preventDefault();
    const {
      target: { name, value },
    } = event;

    if (value !== none) {
      const { i18n } = this.props;
      const lang = countries.find(c => c.id === value).language;
      i18n.changeLanguage(lang);
    }
    this.saveData(USER.country, value);

    this.setState({
      [name]: value,
    });
  };

  /** Fake navigation to Accept Terms and Conditions page */
  handleNavigation = (event) => {
    event.preventDefault();
    this.setState({
      redirectToTermsAndConditions: true,
    });
  };

  handleAcceptTermsClick = (event) => {
    event.preventDefault();
    const dateAccepted = new Date().toISOString();
    this.saveData(USER.acceptedTermsAndConditions, dateAccepted);
    this.setState({
      redirectToTermsAndConditions: false,
      acceptedTermsAndConditions: true,
      acceptedDate: dateAccepted,
    });
  };

  render() {
    const { t, i18n } = this.props;
    const {
      country,
      acceptedTermsAndConditions,
      redirectToTermsAndConditions,
      acceptedDate,
    } = this.state;
    const { language } = i18n;

    let isDisabled = true;
    if (acceptedTermsAndConditions && !!acceptedDate) {
      if (Date.parse(termsAndConditions.date) < Date.parse(acceptedDate)) {
        isDisabled = country === none;
      }
    }
    const btnTitle = (!acceptedTermsAndConditions && t('startPage.readTerms'))
      || (country === none && t('startPage.selectCountryDropdown'))
      || '';
    return (
      <>
        {redirectToTermsAndConditions ? (
          <TermsAndConditionsPage
            onClick={this.handleAcceptTermsClick}
            acceptedDate={acceptedDate ? new Date(acceptedDate) : null}
          />
        ) : (
          <div className="startpage-container px-2">
            <div className="container px-3">
              <div className="text-center">
                <img
                  src={logo}
                  className="my-3 img-fluid cst-logo"
                  alt="CST Logo"
                />
                <h1 className="app-name">
                  Candidate Selection Tool
                </h1>
              </div>
              <form id="setup-form" onSubmit={this.handleSubmit}>
                <div className="form-group">
                  <label htmlFor="country">{t('startPage.selectCountry')}</label>
                  <select
                    required
                    className="form-control"
                    name="country"
                    id="country"
                    value={country}
                    onChange={this.handleChange}
                  >
                    <option key={none} value={none}>
                      {t('startPage.selectCountryDropdown')}
                    </option>
                    {this.countriesOptions}
                  </select>
                </div>
                <div className="mx-5 p-2" />
                <DataProtectionStatement
                  language={language}
                  handleNavigation={this.handleNavigation}
                />
              </form>
            </div>
            {isMobileOnly ? (
              <StartButton disabled={isDisabled} title={btnTitle} />
            ) : (
              <div className="d-flex justify-content-center">
                <StartButton disabled={isDisabled} title={btnTitle} />
              </div>
            )}
          </div>
        )}
      </>
    );
  }
}

StartPage.propTypes = {
  onReady: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withTranslation()(StartPage);
